/**
 * Auth token management.
 *
 * Reads/writes Cognito tokens in localStorage. We store three things:
 *   - The id token (used as the API Bearer credential).
 *   - The refresh token (used to mint a new id token when the current one
 *     expires; lasts much longer — see CDK `refreshTokenValidity`).
 *   - The expiry timestamp (UNIX ms) so authFetch can refresh proactively
 *     before the next API call rather than after it 401s.
 */
const TOKEN_KEY = 'webportal.authToken';
const USER_KEY = 'webportal.authUser';
const REFRESH_KEY = 'webportal.refreshToken';
const EXPIRY_KEY = 'webportal.tokenExpiryMs';
const ACTIVE_ACCOUNT_KEY = 'webportal.activeAccountId';

export interface StoredUser {
  email: string;
}

function readLs(key: string): string | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function writeLs(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore storage errors */
  }
}

function removeLs(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Access (id) token
// ---------------------------------------------------------------------------

export function getAuthToken(): string | null {
  return readLs(TOKEN_KEY);
}

export function setAuthToken(token: string): void {
  writeLs(TOKEN_KEY, token);
}

export function clearAuthToken(): void {
  removeLs(TOKEN_KEY);
}

// ---------------------------------------------------------------------------
// Refresh token
// ---------------------------------------------------------------------------

export function getRefreshToken(): string | null {
  return readLs(REFRESH_KEY);
}

export function setRefreshToken(token: string): void {
  writeLs(REFRESH_KEY, token);
}

export function clearRefreshToken(): void {
  removeLs(REFRESH_KEY);
}

// ---------------------------------------------------------------------------
// Expiry timestamp (UNIX ms)
// ---------------------------------------------------------------------------

export function getTokenExpiryMs(): number | null {
  const raw = readLs(EXPIRY_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Set the absolute expiry timestamp from a Cognito `expiresIn` value
 * (seconds). Subtract a small skew so we refresh slightly early.
 */
export function setTokenExpiryFromSeconds(expiresIn: number): void {
  const skewMs = 30_000; // refresh 30s before actual expiry
  const expiresAt = Date.now() + Math.max(0, expiresIn * 1000 - skewMs);
  writeLs(EXPIRY_KEY, String(expiresAt));
}

export function clearTokenExpiry(): void {
  removeLs(EXPIRY_KEY);
}

/** True when there's no expiry stored, or the stored expiry has passed. */
export function isTokenExpired(now: number = Date.now()): boolean {
  const expiry = getTokenExpiryMs();
  if (expiry === null) return false; // legacy login without expiry — treat as fresh
  return now >= expiry;
}

// ---------------------------------------------------------------------------
// Stored user (display only)
// ---------------------------------------------------------------------------

export function getStoredUser(): StoredUser | null {
  const raw = readLs(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredUser;
  } catch {
    return null;
  }
}

export function setStoredUser(user: StoredUser): void {
  writeLs(USER_KEY, JSON.stringify(user));
}

export function clearStoredUser(): void {
  removeLs(USER_KEY);
}

// ---------------------------------------------------------------------------
// Active account (multi-tenant workspace selector)
// ---------------------------------------------------------------------------

export function getActiveAccountId(): string | null {
  return readLs(ACTIVE_ACCOUNT_KEY);
}

export function setActiveAccountId(id: string): void {
  writeLs(ACTIVE_ACCOUNT_KEY, id);
}

export function clearActiveAccountId(): void {
  removeLs(ACTIVE_ACCOUNT_KEY);
}

// ---------------------------------------------------------------------------
// Combined helpers used by AuthContext + authFetch
// ---------------------------------------------------------------------------

/** Clear everything auth-related from storage in one call. */
export function clearAllAuth(): void {
  clearAuthToken();
  clearRefreshToken();
  clearTokenExpiry();
  clearStoredUser();
  clearActiveAccountId();
}

// ---------------------------------------------------------------------------
// Global logout event — authFetch dispatches this when refresh fails so
// AuthProvider can clear React state + redirect to /login regardless of
// which component triggered the request.
// ---------------------------------------------------------------------------

export const AUTH_LOGOUT_EVENT = 'webportal:auth-logout';

export interface AuthLogoutDetail {
  reason: 'token-expired' | 'refresh-failed' | 'manual';
}

export function dispatchAuthLogout(detail: AuthLogoutDetail): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent<AuthLogoutDetail>(AUTH_LOGOUT_EVENT, { detail }));
  } catch {
    /* test environments without CustomEvent constructor */
  }
}
